import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiMinus, FiDollarSign, FiTruck, FiUser, FiMapPin, FiShoppingCart, FiSearch } from 'react-icons/fi';

const ManualOrders = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    user_id: '',
    cart_items: [{ 
      cart_id: '',
      product_id: '', 
      name: '', 
      size: '', 
      quantity: 1, 
      unit_price: 0, 
      total_price: 0,
      image: '' 
    }],
    billing_info: {
      fullName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip: ''
    },
    shipping_info: {
      address: '',
      city: '',
      state: '',
      zip: ''
    },
    order_summary: {
      subtotal: 0,
      shippingCost: 0,
      total: 0
    },
    status: 'pending',
    payment_status: 'pending'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
      // Reset search term when modal opens
      setSearchTerm('');
    }
  }, [isOpen]);

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await fetch(`${API_BASE_URL}/list-products`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Fetched products:', data); // Debug log
      setProducts(data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      alert('Failed to load products. Please try again.');
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNestedChange = (parent, e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [name]: value
      }
    }));
  };

  const generateCartId = () => {
    return 'cart_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };

  const handleCartItemChange = (index, e) => {
    const { name, value } = e.target;
    const updatedCartItems = [...formData.cart_items];
    updatedCartItems[index] = {
      ...updatedCartItems[index],
      [name]: name === 'quantity' || name === 'unit_price' ? Number(value) : value
    };

    // Calculate total price
    if (name === 'quantity' || name === 'unit_price') {
      updatedCartItems[index].total_price = 
        updatedCartItems[index].quantity * updatedCartItems[index].unit_price;
    }

    setFormData(prev => ({
      ...prev,
      cart_items: updatedCartItems
    }));

    calculateOrderSummary(updatedCartItems, formData.shipping_info.state);
  };

  const handleProductSelect = (product, size) => {
    console.log('Selected product:', product, 'Size:', size); // Debug log
    
    const price = parseFloat(product.prices[size]);
    const updatedCartItems = [...formData.cart_items];
    
    updatedCartItems[selectedItemIndex] = {
      ...updatedCartItems[selectedItemIndex],
      cart_id: generateCartId(),
      product_id: product.id,
      name: product.name,
      size: size,
      unit_price: price,
      total_price: updatedCartItems[selectedItemIndex].quantity * price,
      image: product.images && product.images.length > 0 ? product.images[0] : ''
    };

    setFormData(prev => ({
      ...prev,
      cart_items: updatedCartItems
    }));

    calculateOrderSummary(updatedCartItems, formData.shipping_info.state);
    setShowProductModal(false);
    setSelectedItemIndex(null);
    setSearchTerm(''); // Reset search term
  };

  const openProductModal = (index) => {
    console.log('Opening product modal for index:', index); // Debug log
    setSelectedItemIndex(index);
    setShowProductModal(true);
    setSearchTerm(''); // Reset search term when opening modal
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setSelectedItemIndex(null);
    setSearchTerm('');
  };

  const addCartItem = () => {
    setFormData(prev => ({
      ...prev,
      cart_items: [
        ...prev.cart_items,
        { 
          cart_id: '',
          product_id: '', 
          name: '', 
          size: '', 
          quantity: 1, 
          unit_price: 0, 
          total_price: 0,
          image: '' 
        }
      ]
    }));
  };

  const removeCartItem = (index) => {
    if (formData.cart_items.length <= 1) return;
    const updatedCartItems = formData.cart_items.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      cart_items: updatedCartItems
    }));
    calculateOrderSummary(updatedCartItems, formData.shipping_info.state);
  };

  const calculateShippingCost = (state) => {
    return state && state.toLowerCase() === 'kerala' ? 60 : 120;
  };

  const calculateOrderSummary = (cartItems, state) => {
    const subtotal = cartItems.reduce((sum, item) => sum + item.total_price, 0);
    const shippingCost = calculateShippingCost(state);
    const total = subtotal + shippingCost;

    setFormData(prev => ({
      ...prev,
      order_summary: {
        subtotal,
        shippingCost,
        total
      }
    }));
  };

  const handleShippingStateChange = (e) => {
    const { value } = e.target;
    handleNestedChange('shipping_info', e);
    calculateOrderSummary(formData.cart_items, value);
  };

  const copyBillingToShipping = () => {
    setFormData(prev => ({
      ...prev,
      shipping_info: {
        address: prev.billing_info.address,
        city: prev.billing_info.city,
        state: prev.billing_info.state,
        zip: prev.billing_info.zip
      }
    }));
    calculateOrderSummary(formData.cart_items, formData.billing_info.state);
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate user_id
    if (!formData.user_id.trim()) {
      newErrors.user_id = 'Customer email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.user_id)) {
      newErrors.user_id = 'Email is invalid';
    }

    // Validate cart items
    formData.cart_items.forEach((item, index) => {
      if (!item.product_id.trim()) {
        newErrors[`cart_items_${index}_product_id`] = 'Please select a product';
      }
      if (!item.name.trim()) {
        newErrors[`cart_items_${index}_name`] = 'Product name is required';
      }
      if (!item.size.trim()) {
        newErrors[`cart_items_${index}_size`] = 'Product size is required';
      }
      if (item.quantity <= 0) {
        newErrors[`cart_items_${index}_quantity`] = 'Quantity must be greater than 0';
      }
      if (item.unit_price <= 0) {
        newErrors[`cart_items_${index}_unit_price`] = 'Unit price must be greater than 0';
      }
    });

    // Validate billing info
    const billingRequired = ['fullName', 'email', 'phone', 'address', 'city', 'state', 'zip'];
    billingRequired.forEach(field => {
      if (!formData.billing_info[field].trim()) {
        newErrors[`billing_${field}`] = `${field === 'fullName' ? 'Full name' : field} is required`;
      }
    });

    // Validate email format in billing info
    if (formData.billing_info.email && !/\S+@\S+\.\S+/.test(formData.billing_info.email)) {
      newErrors.billing_email = 'Email is invalid';
    }

    // Validate shipping info
    const shippingRequired = ['address', 'city', 'state', 'zip'];
    shippingRequired.forEach(field => {
      if (!formData.shipping_info[field].trim()) {
        newErrors[`shipping_${field}`] = `${field} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      console.log('Submitting order data:', formData); // Debug log
      
      const response = await fetch(`${API_BASE_URL}/create-manual-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Order created successfully:', result);
        alert('Order created successfully!');
        if (onSubmit) onSubmit(result);
        
        // Reset form
        setFormData({
          user_id: '',
          cart_items: [{ 
            cart_id: '',
            product_id: '', 
            name: '', 
            size: '', 
            quantity: 1, 
            unit_price: 0, 
            total_price: 0,
            image: '' 
          }],
          billing_info: {
            fullName: '',
            email: '',
            phone: '',
            address: '',
            city: '',
            state: '',
            zip: ''
          },
          shipping_info: {
            address: '',
            city: '',
            state: '',
            zip: ''
          },
          order_summary: {
            subtotal: 0,
            shippingCost: 0,
            total: 0
          },
          status: 'pending',
          payment_status: 'pending'
        });
        
        onClose();
      } else {
        const errorData = await response.json();
        console.error('Error creating order:', errorData);
        alert('Error creating order: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      alert('Error submitting order: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Main Modal Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          onClick={onClose}
        />
        
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
        
        {/* Main Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full relative z-10">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-start">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Create Manual Order
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="mt-4 space-y-6">
              {/* Customer Section */}
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                  <FiUser className="mr-2" /> Customer Information
                </h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="user_id" className="block text-sm font-medium text-gray-700">
                      Customer Email *
                    </label>
                    <input
                      type="email"
                      id="user_id"
                      name="user_id"
                      value={formData.user_id}
                      onChange={handleChange}
                      placeholder="customer@example.com"
                      className={`mt-1 block w-full border ${errors.user_id ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                    />
                    {errors.user_id && <p className="mt-1 text-sm text-red-600">{errors.user_id}</p>}
                  </div>
                </div>
              </div>

              {/* Cart Items Section */}
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-md font-medium text-gray-900 flex items-center">
                    <FiShoppingCart className="mr-2" /> Order Items
                  </h4>
                  <button
                    type="button"
                    onClick={addCartItem}
                    className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <FiPlus className="mr-1" /> Add Item
                  </button>
                </div>

                {formData.cart_items.map((item, index) => (
                  <div key={index} className="mb-6 border-b border-gray-200 pb-4 last:border-0 last:mb-0 last:pb-0">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="text-sm font-medium text-gray-700">Item #{index + 1}</h5>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Button clicked for index:', index); // Debug log
                            openProductModal(index);
                          }}
                          className="inline-flex items-center px-3 py-1 border border-indigo-300 text-sm font-medium rounded text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <FiSearch className="mr-1 h-3 w-3" /> 
                          {item.product_id ? 'Change Product' : 'Select Product'}
                        </button>
                        {formData.cart_items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeCartItem(index)}
                            className="text-red-600 hover:text-red-800 text-sm flex items-center"
                          >
                            <FiMinus className="mr-1" /> Remove
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                      <div>
                        <label htmlFor={`product_id_${index}`} className="block text-sm font-medium text-gray-700">
                          Product ID *
                        </label>
                        <input
                          type="text"
                          id={`product_id_${index}`}
                          name="product_id"
                          value={item.product_id}
                          readOnly
                          placeholder="Select a product"
                          className={`mt-1 block w-full border ${errors[`cart_items_${index}_product_id`] ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 bg-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                        />
                        {errors[`cart_items_${index}_product_id`] && (
                          <p className="mt-1 text-sm text-red-600">{errors[`cart_items_${index}_product_id`]}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor={`name_${index}`} className="block text-sm font-medium text-gray-700">
                          Product Name *
                        </label>
                        <input
                          type="text"
                          id={`name_${index}`}
                          name="name"
                          value={item.name}
                          readOnly
                          placeholder="Product name will appear here"
                          className={`mt-1 block w-full border ${errors[`cart_items_${index}_name`] ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 bg-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                        />
                        {errors[`cart_items_${index}_name`] && (
                          <p className="mt-1 text-sm text-red-600">{errors[`cart_items_${index}_name`]}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor={`size_${index}`} className="block text-sm font-medium text-gray-700">
                          Size/Variant *
                        </label>
                        <input
                          type="text"
                          id={`size_${index}`}
                          name="size"
                          value={item.size}
                          readOnly
                          placeholder="Size will appear here"
                          className={`mt-1 block w-full border ${errors[`cart_items_${index}_size`] ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 bg-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                        />
                        {errors[`cart_items_${index}_size`] && (
                          <p className="mt-1 text-sm text-red-600">{errors[`cart_items_${index}_size`]}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor={`quantity_${index}`} className="block text-sm font-medium text-gray-700">
                          Quantity *
                        </label>
                        <input
                          type="number"
                          id={`quantity_${index}`}
                          name="quantity"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleCartItemChange(index, e)}
                          className={`mt-1 block w-full border ${errors[`cart_items_${index}_quantity`] ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                        />
                        {errors[`cart_items_${index}_quantity`] && (
                          <p className="mt-1 text-sm text-red-600">{errors[`cart_items_${index}_quantity`]}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor={`unit_price_${index}`} className="block text-sm font-medium text-gray-700">
                          Unit Price (₹) *
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">₹</span>
                          </div>
                          <input
                            type="number"
                            id={`unit_price_${index}`}
                            name="unit_price"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            readOnly
                            placeholder="0.00"
                            className={`block w-full pl-7 border ${errors[`cart_items_${index}_unit_price`] ? 'border-red-300' : 'border-gray-300'} rounded-md py-2 px-3 bg-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                          />
                        </div>
                        {errors[`cart_items_${index}_unit_price`] && (
                          <p className="mt-1 text-sm text-red-600">{errors[`cart_items_${index}_unit_price`]}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Total Price
                        </label>
                        <div className="mt-1 px-3 py-2 bg-gray-100 rounded-md text-sm">
                          ₹{item.total_price.toFixed(2)}
                        </div>
                      </div>

                      {item.image && (
                        <div className="sm:col-span-3">
                          <label className="block text-sm font-medium text-gray-700">
                            Product Image
                          </label>
                          <div className="mt-1">
                            <img 
                              src={item.image} 
                              alt={item.name}
                              className="h-20 w-20 object-cover rounded-md border"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Billing Information */}
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                  <FiMapPin className="mr-2" /> Billing Information
                </h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {Object.entries(formData.billing_info).map(([field, value]) => (
                    <div key={`billing_${field}`}>
                      <label htmlFor={`billing_${field}`} className="block text-sm font-medium text-gray-700">
                        {field === 'fullName' ? 'Full Name' : field === 'zip' ? 'ZIP Code' : field.charAt(0).toUpperCase() + field.slice(1)} *
                      </label>
                      <input
                        type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                        id={`billing_${field}`}
                        name={field}
                        value={value}
                        onChange={(e) => handleNestedChange('billing_info', e)}
                        placeholder={`Enter ${field === 'fullName' ? 'full name' : field}`}
                        className={`mt-1 block w-full border ${errors[`billing_${field}`] ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                      />
                      {errors[`billing_${field}`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`billing_${field}`]}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping Information */}
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-md font-medium text-gray-900 flex items-center">
                    <FiTruck className="mr-2" /> Shipping Information
                  </h4>
                  <button
                    type="button"
                    onClick={copyBillingToShipping}
                    className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Copy from Billing
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {Object.entries(formData.shipping_info).map(([field, value]) => (
                    <div key={`shipping_${field}`}>
                      <label htmlFor={`shipping_${field}`} className="block text-sm font-medium text-gray-700">
                        {field === 'zip' ? 'ZIP Code' : field.charAt(0).toUpperCase() + field.slice(1)} *
                      </label>
                      <input
                        type="text"
                        id={`shipping_${field}`}
                        name={field}
                        value={value}
                        onChange={field === 'state' ? handleShippingStateChange : (e) => handleNestedChange('shipping_info', e)}
                        placeholder={`Enter ${field}`}
                        className={`mt-1 block w-full border ${errors[`shipping_${field}`] ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                      />
                      {errors[`shipping_${field}`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`shipping_${field}`]}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                  <FiDollarSign className="mr-2" /> Order Summary
                </h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Subtotal</label>
                    <div className="mt-1 px-3 py-2 bg-gray-100 rounded-md text-sm">
                      ₹{formData.order_summary.subtotal.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Shipping Cost</label>
                    <div className="mt-1 px-3 py-2 bg-gray-100 rounded-md text-sm">
                      ₹{formData.order_summary.shippingCost.toFixed(2)}
                      <span className="text-xs text-gray-500 ml-1">
                        ({formData.shipping_info.state?.toLowerCase() === 'kerala' ? 'Kerala' : 'Other states'})
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Total</label>
                    <div className="mt-1 px-3 py-2 bg-gray-100 rounded-md text-sm font-semibold">
                      ₹{formData.order_summary.total.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Status */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                    Order Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="payment_status" className="block text-sm font-medium text-gray-700">
                    Payment Status
                  </label>
                  <select
                    id="payment_status"
                    name="payment_status"
                    value={formData.payment_status}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || formData.cart_items.some(item => !item.product_id)}
                  className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                    isSubmitting || formData.cart_items.some(item => !item.product_id)
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : 'Create Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Product Selection Modal */}
      {showProductModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Product Modal Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" 
              onClick={closeProductModal}
            />
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            
            {/* Product Modal */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full relative z-10">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Select Product for Item #{selectedItemIndex !== null ? selectedItemIndex + 1 : ''}
                  </h3>
                  <button
                    onClick={closeProductModal}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <FiX className="h-6 w-6" />
                  </button>
                </div>

                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Search by product name, ID, or category..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                {/* Products List */}
                <div className="max-h-96 overflow-y-auto">
                  {loadingProducts ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                      <p className="mt-2 text-gray-500">Loading products...</p>
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">
                        {searchTerm ? 'No products found matching your search.' : 'No products available.'}
                      </p>
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm('')}
                          className="mt-2 text-indigo-600 hover:text-indigo-800 text-sm"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredProducts.map((product) => (
                        <div key={product._id || product.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                          <div className="flex items-start space-x-4">
                            {product.images && product.images[0] && (
                              <img 
                                src={product.images[0]} 
                                alt={product.name}
                                className="h-20 w-20 object-cover rounded-md border"
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00MCA1NkMzNi42ODYzIDU2IDM0IDUzLjMxMzcgMzQgNTBDMzQgNDYuNjg2MyAzNi42ODYzIDQ0IDQwIDQ0QzQzLjMxMzcgNDQgNDYgNDYuNjg2MyA0NiA1MEM0NiA1My4zMTM3IDQzLjMxMzcgNTYgNDAgNTZaIiBmaWxsPSIjOUM5Q0EzIi8+CjxwYXRoIGQ9Ik0yNCAyNEg1NkM1Ny4xMDQ2IDI0IDU4IDI0Ljg5NTQgNTggMjZWNTRDNTggNTUuMTA0NiA1Ny4xMDQ2IDU2IDU2IDU2SDI0QzIyLjg5NTQgNTYgMjIgNTUuMTA0NiAyMiA1NFYyNkMyMiAyNC44OTU0IDIyLjg5NTQgMjQgMjQgMjRaIiBzdHJva2U9IiM5QzlDQTMiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0ibm9uZSIvPgo8L3N2Zz4K';
                                }}
                              />
                            )}
                            <div className="flex-1">
                              <h4 className="text-base font-medium text-gray-900">{product.name}</h4>
                              <p className="text-sm text-gray-500">ID: {product.id}</p>
                              <p className="text-sm text-gray-500">Category: {product.category}</p>
                              <p className="text-sm text-gray-500">Stock: {product.stockAvailability}</p>
                              
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Available Sizes & Prices:</p>
                                <div className="flex flex-wrap gap-2">
                                  {Object.entries(product.prices).map(([size, price]) => (
                                    <button
                                      key={size}
                                      onClick={() => handleProductSelect(product, size)}
                                      disabled={product.stockAvailability === 'Out of stock'}
                                      className={`inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md transition-colors ${
                                        product.stockAvailability === 'Out of stock'
                                          ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
                                          : 'border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                                      }`}
                                    >
                                      {size} - ₹{price}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualOrders;